import React from 'react';

/**
 * 02/06/2026 — Page-level error boundary, used initially to wrap the
 * Appels d'Offres page (user reported a black screen in production).
 *
 * When a render error happens, the boundary catches it and renders a
 * recovery card with the error message + a "Retry" button instead of
 * letting the whole route unmount silently. We also log to the console
 * so that the error remains visible to anyone opening DevTools.
 */
export default class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message || String(error),
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[PageErrorBoundary]', this.props.label || 'page', error, info?.componentStack);
  }

  reset = () => this.setState({ hasError: false, errorMessage: null });

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="bg-ink-800 rounded-2xl p-6 border border-rose-500/40 max-w-2xl mx-auto"
          data-testid="page-error-boundary"
        >
          <h2 className="text-xl font-display font-bold text-rose-300 mb-2">
            Erreur d'affichage
          </h2>
          <p className="text-sm text-slate-300 mb-3">
            Une erreur empêche le rendu de cette page. Le jeu reste sauvegardé.
          </p>
          <pre className="text-[10px] text-rose-200/80 bg-rose-950/40 border border-rose-500/30 rounded p-2 overflow-x-auto whitespace-pre-wrap">
            {this.state.errorMessage}
          </pre>
          <div className="flex gap-2 mt-3">
            <button
              onClick={this.reset}
              className="px-4 py-1.5 rounded bg-accent text-white text-xs font-bold hover:bg-accent-hover"
              data-testid="error-boundary-retry"
            >
              Réessayer
            </button>
            <button
              onClick={() => { window.location.href = '/dashboard'; }}
              className="px-4 py-1.5 rounded border border-ink-600 text-slate-300 text-xs font-semibold hover:bg-ink-700/60"
              data-testid="error-boundary-back"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
